import { motion, AnimatePresence } from "framer-motion";
import { BOT_PROFILES } from "@/lib/botProfiles";
import bobosMascot from "@/assets/bobos-mascot.webp";
import { useState, useEffect, useMemo } from "react";
import { useRecentWinner } from "@/hooks/useRecentWinner";

type SpotlightEntry = {
  displayName: string;
  avatarUrl: string | null;
  totalWins: number;
  totalGames: number;
  games24h: number;
  level: number;
  game?: string | null;
  statScope?: "all" | "tavli";
};

const botAvatarMap = new Map(BOT_PROFILES.map((b) => [b.id, b.avatarUrl]));

interface SpotlightProps {
  spotlightData?: { real: any[]; npc: any[]; mostActive?: any; recentWinner?: any };
  isLoading?: boolean;
}

const spotlightBadge = (wins: number) => {
  if (wins >= 500) return "👑 Θρύλος";
  if (wins >= 250) return "🏆 Βετεράνος";
  if (wins >= 100) return "🔥 Έμπειρος";
  if (wins >= 25) return "🎯 Τακτικός";
  if (wins >= 5) return "🎲 Παίκτης";
  return "🌱 Νέο Μέλος";
};

const SpotlightFace = ({ player, label }: { player: SpotlightEntry | null; label: string }) => {
  if (!player) {
    return (
      <div className="flex flex-col items-center">
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-black shadow-2xl border-4 border-yellow-500 flex items-center justify-center overflow-hidden">
          <img src={bobosMascot} alt="Bob" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
        </div>
      </div>
    );
  }

  const winRate = player.totalGames > 0
    ? Math.round((player.totalWins / player.totalGames) * 100)
    : 0;

  return (
    <div className="flex flex-col items-center">
      <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-black shadow-2xl border-4 border-yellow-500 flex items-center justify-center overflow-hidden">
        {player.avatarUrl ? (
          <img src={player.avatarUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-4xl">👤</span>
        )}
      </div>

      <div className="mt-3 flex flex-col items-center">
        <div className="bg-black border border-yellow-500 rounded-2xl px-4 py-1.5 shadow-lg text-center min-w-[140px] max-w-[200px]">
          <p className="font-display font-bold text-sm text-white truncate">
            {player.displayName}
          </p>
          <p className="text-[11px] text-white/90 font-body leading-tight">
            {label === "🎲 Νέο Μέλος" ? spotlightBadge(player.totalWins) : label}
            {player.game ? ` · ${player.game}` : ""}
            {" · "}
            {player.statScope === "tavli"
              ? `${player.totalWins} νίκες Ταβλιού`
              : `Σύνολο ${player.totalWins} νίκες`}
          </p>
        </div>
      </div>
    </div>
  );
};

const PlayerSpotlight = ({ spotlightData, isLoading = false }: SpotlightProps) => {
  const [showActive, setShowActive] = useState(false);
  const { data: freshWinner } = useRecentWinner();

  const { randomPlayer, activePlayer } = useMemo(() => {
    if (!spotlightData) return { randomPlayer: null, activePlayer: null };


    const realPlayers: SpotlightEntry[] = (spotlightData.real || [])
      .filter((p: any) => p.display_name && !p.display_name.startsWith("Παίκτης #"))
      .map((p: any) => ({
        displayName: p.display_name || p.username || "Ανώνυμος",
        avatarUrl: p.avatar_url,
        totalWins: p.total_wins,
        totalGames: p.total_games,
        games24h: p.games_24h || 0,
        level: p.level,
      }));

    const npcPlayers: SpotlightEntry[] = (spotlightData.npc || [])
      .filter((n: any) => {
        const name = (n.display_name || n.username || "").toLowerCase();
        return name !== "bob" && name !== "μπομπ";
      })
      .map((n: any) => ({
        displayName: n.display_name || n.username || "NPC",
        avatarUrl: botAvatarMap.get(n.id) || null,
        totalWins: n.total_wins,
        totalGames: n.total_games,
        games24h: n.games_24h || 0,
        level: n.level,
      }));

    const pool = [...realPlayers, ...npcPlayers];

    const random = pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : null;

    // Πάντα φρέσκος νικητής από το ανεξάρτητο query· fallback στο boot payload
    const rw = freshWinner || spotlightData.recentWinner;
    const active: SpotlightEntry | null = rw
      ? {
          displayName: rw.display_name || rw.username || "Παίκτης",
          avatarUrl: rw.avatar_url || botAvatarMap.get(rw.id) || null,
          totalWins: rw.total_wins,
          totalGames: rw.total_games,
          games24h: 0,
          level: rw.level,
          game: rw.game || null,
          statScope: rw.stat_scope === "tavli" ? "tavli" : "all",
        }
      : null;

    return { randomPlayer: random, activePlayer: active };
  }, [spotlightData, freshWinner]);


  // Flip between sides every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShowActive((prev) => !prev);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-black shadow-2xl border-4 border-yellow-500 flex items-center justify-center overflow-hidden">
          <img src={bobosMascot} alt="Bob" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="flex flex-col items-center"
    >
      <AnimatePresence mode="wait">
        {showActive ? (
          <motion.div
            key="active"
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SpotlightFace player={activePlayer || null} label="🏅 Πρόσφατα νίκησε" />
          </motion.div>
        ) : (
          <motion.div
            key="random"
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SpotlightFace player={randomPlayer || null} label="🎲 Νέο Μέλος" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PlayerSpotlight;
